import { useState } from "react";
import { PaymentFrequency } from "../../common/interfaces/enums/PaymentFrequency";
import ProductType, {
    ClientProductTypesList
} from "../../common/interfaces/enums/ProductType";
import { QuoteTableView } from "../enums/QuoteTableView";
import SortBy from "../enums/SortBy";
import { IClient } from "../../common/interfaces/IClient";
import { IPolicyFeature } from "../interfaces/IPolicyFeature";
import IQuoteCardProduct from "../interfaces/results/IQuoteCardProduct";
import IQuoteCardResult from "../interfaces/results/IQuoteCardResult";
import { getSortByAsString } from "../../common/shared/enumHelpers";
import QuoteTableState, {
    GlobalQuoteTableState
} from "../types/quoteTable/IQuoteTableState";
import {
    QuoteTableCellInfo,
    QuoteTableSectionInfoStandalone
} from "../types/quoteTable/QuoteTableSectionInfo";
import useCart from "../../common/hooks/useCart";
import QuoteTableHeaderContent from "../components/Quotes/QuoteTable/Cells/QuoteTableHeaderContent";
import QuoteTableHeaderStickyContent from "../components/Quotes/QuoteTable/Cells/QuoteTableHeaderStickyContent";
import QuoteTableInsurerInfoContent from "../components/Quotes/QuoteTable/Cells/QuoteTableInsurerInfoContent/QuoteTableInsurerInfoContent";
import QuoteTableOffersAndBenefitsContent from "../components/Quotes/QuoteTable/Cells/QuoteTableOffersAndBenefitsContent/QuoteTableOffersAndBenefitsContent";
import QuoteTableProductContent from "../components/Quotes/QuoteTable/Cells/QuoteTableProductContent";
import QuoteTableFinancialStrengthContent from "../components/Quotes/QuoteTable/Cells/QuoteTableFinancialStrengthContent";
import QuoteTableStarRatingContent from "../components/Quotes/QuoteTable/Cells/QuoteTableStarRatingContent";
import QuoteTablePolicyFeatureContent from "../components/Quotes/QuoteTable/Cells/QuoteTablePolicyFeatureContent";
import QuoteTableViewSwitcherContent from "../components/Quotes/QuoteTable/Cells/QuoteTableViewSwitcherContent";
import QuoteTableDocumentContent from "../components/Quotes/QuoteTable/Cells/QuoteTableDocumentContent";
import QuoteTableFooterContent from "../components/Quotes/QuoteTable/Cells/QuoteTableFooterContent";

export type QuoteTableCallToActionFunction = (productId: number) => Promise<void>;

export type QuoteTableAdminActionFunction = (
    isSelected: boolean,
    productId: number
) => Promise<void>;

interface IQuoteTableReturn {
    // Section Builders
    getQuoteTableHeaderInfo: (
        cards: IQuoteCardResult[],
        sortBy: SortBy,
        clients: IClient[],
        handleCallToAction: QuoteTableCallToActionFunction,
        handlePaymentFrequencyChange: (paymentFreq: PaymentFrequency) => Promise<void>
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableHeaderContent>;
    getQuoteTableHeaderStickyInfo: (
        cards: IQuoteCardResult[],
        sortBy: SortBy,
        handleCallToAction: QuoteTableCallToActionFunction,
        handlePaymentFrequencyChange: (paymentFreq: PaymentFrequency) => Promise<void>
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableHeaderStickyContent>;
    getInsurerInfo: (
        cards: IQuoteCardResult[],
        productType: ProductType
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableInsurerInfoContent>;
    getOffersAndBenefitsInfo: (
        cards: IQuoteCardResult[],
        hasInlineTitle: boolean
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableOffersAndBenefitsContent>;
    getProductInfo: (
        cards: IQuoteCardResult[],
        productType: ProductType
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableProductContent>;
    getFinancialStrengthInfo: (
        cards: IQuoteCardResult[]
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableFinancialStrengthContent>;
    getCustomerServiceInfo: (
        cards: IQuoteCardResult[],
        handleClickCustomerServiceModal: () => void
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableStarRatingContent>;
    getPolicyFeaturesInfo: (
        cards: IQuoteCardResult[],
        handleClickPolicyFeaturesModal: () => void
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableStarRatingContent>;
    getFeatureSectionInfoList: (
        cards: IQuoteCardResult[],
        productType: ProductType,
        onlyDifferences: boolean
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTablePolicyFeatureContent>[];
    getViewSwitcherInfo: (
        cards: IQuoteCardResult[],
        currentView: QuoteTableView,
        setView: (newView: QuoteTableView) => void
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableViewSwitcherContent>;
    getDocumentInfo: (
        cards: IQuoteCardResult[],
        productType: ProductType
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableDocumentContent>;
    getFooterInfo: (
        cards: IQuoteCardResult[],
        handleCallToAction: QuoteTableCallToActionFunction,
        currentView: QuoteTableView,
        setView: (newView: QuoteTableView) => void
    ) => QuoteTableSectionInfoStandalone<typeof QuoteTableFooterContent>;
    getProductDetailsList: (cards: IQuoteCardResult[]) => ProductType[];
    setNewGlobalState: (newGlobalState: GlobalQuoteTableState) => void;
    globalState: GlobalQuoteTableState;
    createQuoteTableState: (
        result: IQuoteCardResult,
        globalState: GlobalQuoteTableState
    ) => QuoteTableState;
}

const useQuoteSections = (
    groupId: string,
    initialGlobalState: GlobalQuoteTableState
): IQuoteTableReturn => {
    const [globalState, setNewGlobalState] = useState<GlobalQuoteTableState>(
        initialGlobalState
    );

    const { generateApplicationURL } = useCart();

    /**
     *
     * Helper Functions
     *
     */

    const createKey = (card: IQuoteCardResult): string => {
        return (
            card.result.providerId +
            "-" +
            Object.values(card.productInfo)
                .map((s) => s.productId)
                .join(";")
        );
    };

    const createQuoteTableState = (
        result: IQuoteCardResult,
        globalState: GlobalQuoteTableState
    ): QuoteTableState => {
        return {
            ...result.state,
            ...globalState
        };
    };

    const getAllPolicyFeatureSectionIds = (
        results: IQuoteCardResult[],
        productType: ProductType,
    ): number[] => {
        const sectionIds: {
            sectionId: number;
            sortOrder: number;
        }[] = [];

        results.forEach((result) => {
            result.productInfo[productType]?.policyFeatures.forEach((feature) => {
                if (!sectionIds.some((s) => s.sectionId === feature.sectionId)) {
                    sectionIds.push({
                        sectionId: feature.sectionId,
                        sortOrder: feature.orderBy
                    });
                }
            });
        });

        return sectionIds
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((s) => s.sectionId);
    };

    interface ISectionResultType extends IPolicyFeature {
        key: string | number;
        quoteState: QuoteTableState;
    }

    const getSections = (
        results: IQuoteCardResult[],
        productType: ProductType,
        sectionId: number
    ): ISectionResultType[] => {
        return results.map((result) => {
            const productInfo: IQuoteCardProduct = result.productInfo[productType];

            const state = createQuoteTableState(result, globalState);

            if (!productInfo)
                return {
                    key: `${result.cardKey}-${sectionId}`,
                    detail: "",
                    detailMore: "",
                    detailShowMore: "",
                    isDifference: false,
                    orderBy: 0,
                    quoteState: state,
                    sectionId,
                    sectionName: "",
                    tooltip: ""
                };

            return {
                key: productInfo.productId,
                quoteState: state,
                ...productInfo.policyFeatures.find(
                    (feature) => feature.sectionId === sectionId
                )
            };
        });
    };

    type ViewSwitcherSettings = {
        target: QuoteTableView;
        text: string;
    };

    const getViewSwitcher = (view: QuoteTableView): ViewSwitcherSettings => {
        // NOTE: \xa0 represents a non-breakable space
        // This stops the space being where the line break can occur, and keeps everything on one line
        const switchViewConfig: {
            [key in QuoteTableView]: ViewSwitcherSettings;
        } = {
            [QuoteTableView.Overview]: {
                text: "View\xa0details",
                target: QuoteTableView.Details
            },
            [QuoteTableView.Details]: {
                text: "View\xa0overview",
                target: QuoteTableView.Overview
            }
        };

        return switchViewConfig[view];
    };

    /**
     *
     * Section Builders
     *
     */

    const getQuoteTableHeaderInfo: IQuoteTableReturn["getQuoteTableHeaderInfo"] = (
        results: IQuoteCardResult[],
        sortBy: SortBy,
        clients: IClient[],
        handleCallToAction: QuoteTableCallToActionFunction,
        handlePaymentFrequencyChange: (paymentFreq: PaymentFrequency) => Promise<void>
    ) => {
        return {
            id: "Header",
            type: "standalone",
            title: null,
            tooltip: null,
            component: QuoteTableHeaderContent,
            renderOnError: true,
            cells: results.map(
                (card): QuoteTableCellInfo<typeof QuoteTableHeaderContent> => {
                    const bannerText = card.state.isHighlighted
                        ? getSortByAsString(sortBy)
                        : "";

                    const quoteState = createQuoteTableState(card, globalState);

                    const props: React.ComponentProps<typeof QuoteTableHeaderContent> = {
                        applicationURL: generateApplicationURL(groupId, card),
                        bannerHeadingText: bannerText,
                        card,
                        clients,
                        handleCallToAction,
                        handlePaymentFrequencyChange,
                        quoteState,
                        groupId
                    };

                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props
                    };
                }
            )
        };
    };

    const getQuoteTableHeaderStickyInfo: IQuoteTableReturn["getQuoteTableHeaderStickyInfo"] = (
        results: IQuoteCardResult[],
        sortBy: SortBy,
        handleSelectProduct: QuoteTableCallToActionFunction,
        handlePaymentFrequencyChange: (paymentFreq: PaymentFrequency) => Promise<void>
    ) => {
        return {
            id: "Sticky Header",
            type: "standalone",
            title: null,
            tooltip: null,
            component: QuoteTableHeaderStickyContent,
            renderOnError: true,
            cells: results.map(
                (card): QuoteTableCellInfo<typeof QuoteTableHeaderStickyContent> => {
                    const bannerText = card.state.isHighlighted
                        ? getSortByAsString(sortBy)
                        : "";
                    const quoteState = createQuoteTableState(card, globalState);
                    return {
                        key: createKey(card),
                        quoteState,
                        props: {
                            applicationURL: generateApplicationURL(groupId, card),
                            bannerHeadingText: bannerText,
                            handlePaymentFrequencyChange,
                            handleSelectProduct,
                            quoteState,
                            result: card,
                            groupId
                        }
                    };
                }
            )
        };
    };

    const getInsurerInfo: IQuoteTableReturn["getInsurerInfo"] = (
        cards: IQuoteCardResult[],
        productType: ProductType
    ) => {
        return {
            id: "InsurerInfo",
            type: "standalone",
            title: null,
            tooltip: null,
            component: QuoteTableInsurerInfoContent,
            renderOnError: false,
            cells: cards.map(
                (card): QuoteTableCellInfo<typeof QuoteTableInsurerInfoContent> => {
                    const productInfo: IQuoteCardProduct = card.productInfo[productType];

                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props: {
                            providerName: card.result.providerName,
                            sellingPoints: productInfo.sellingPoints
                        }
                    };
                }
            )
        };
    };

    const getOffersAndBenefitsInfo: IQuoteTableReturn["getOffersAndBenefitsInfo"] = (
        results: IQuoteCardResult[],
        hasInlineTitle: boolean
    ) => {
        return {
            id: "OffersAndBenefits",
            type: "standalone",
            title: hasInlineTitle ? null : "Offers and Benefits",
            tooltip: null,
            component: QuoteTableOffersAndBenefitsContent,
            renderOnError: false,
            cells: results.map(
                (card): QuoteTableCellInfo<typeof QuoteTableOffersAndBenefitsContent> => {
                    const quoteState = createQuoteTableState(card, globalState);
                    return {
                        key: createKey(card),
                        quoteState,
                        props: {
                            cashbackAmount: card.totalCashback,
                            providerAddons: card.providerAddons,
                            providerId: card.result.providerId,
                            providerName: card.result.providerName,
                            quoteState,
                            specialOffers: card.specialOffers.map(
                                (s) => s.specialOfferName
                            ),
                            hasInlineTitle
                        }
                    };
                }
            )
        };
    };

    const getProductInfo: IQuoteTableReturn["getProductInfo"] = (
        results: IQuoteCardResult[],
        productType: ProductType
    ) => {
        return {
            id: "ProductInfo",
            type: "standalone",
            title: "Product name",
            tooltip: null,
            component: QuoteTableProductContent,
            renderOnError: false,
            cells: results.map(
                (card): QuoteTableCellInfo<typeof QuoteTableProductContent> => {
                    const productInfo: IQuoteCardProduct = card.productInfo[productType];

                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props: {
                            productName: productInfo?.productName || "Unknown"
                        }
                    };
                }
            )
        };
    };

    const getFinancialStrengthInfo: IQuoteTableReturn["getFinancialStrengthInfo"] = (
        results: IQuoteCardResult[]
    ) => {
        return {
            id: "FinancialStrength",
            type: "standalone",
            title: "Financial strength",
            tooltip: null,
            component: QuoteTableFinancialStrengthContent,
            renderOnError: false,
            cells: results.map(
                (
                    card: IQuoteCardResult
                ): QuoteTableCellInfo<typeof QuoteTableFinancialStrengthContent> => {
                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props: {
                            providerId: card.result.providerId,
                            providerName: card.result.providerName,
                            providerRating: {
                                rating: card.ratings.financialStrength.ratingInfo.rating,
                                ratingAgency:
                                    card.ratings.financialStrength.ratingInfo
                                        .ratingAgency,
                                ratingDescription:
                                    card.ratings.financialStrength.ratingInfo
                                        .ratingDescription,
                                ratingOrder:
                                    card.ratings.financialStrength.ratingInfo.ratingOrder
                            }
                        }
                    };
                }
            )
        };
    };

    const getCustomerServiceInfo: IQuoteTableReturn["getCustomerServiceInfo"] = (
        results: IQuoteCardResult[],
        handleClickCustomerServiceModal: () => void
    ) => {
        return {
            id: "CustomerServiceInfo",
            type: "standalone",
            title: "Customer service",
            tooltip: null,
            component: QuoteTableStarRatingContent,
            renderOnError: false,
            learnMoreHandler: handleClickCustomerServiceModal,
            cells: results.map(
                (
                    card: IQuoteCardResult
                ): QuoteTableCellInfo<typeof QuoteTableStarRatingContent> => {
                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props: {
                            rating: card.ratings.customerService.ratingInfo
                        }
                    };
                }
            )
        };
    };

    const getPolicyFeaturesInfo: IQuoteTableReturn["getPolicyFeaturesInfo"] = (
        cards: IQuoteCardResult[],
        handleClickPolicyFeaturesModal: () => void
    ) => {
        return {
            id: "PolicyFeatures",
            type: "standalone",
            title: "Overall policy features",
            tooltip: null,
            learnMoreHandler: handleClickPolicyFeaturesModal,
            renderOnError: false,
            component: QuoteTableStarRatingContent,
            cells: cards.map(
                (
                    card: IQuoteCardResult
                ): QuoteTableCellInfo<typeof QuoteTableStarRatingContent> => {
                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props: {
                            rating: card.ratings.provider.ratingInfo?.totalStarRating
                        }
                    };
                }
            )
        };
    };

    const getFeatureSectionInfoList: IQuoteTableReturn["getFeatureSectionInfoList"] = (
        results: IQuoteCardResult[],
        productType: ProductType,
    ) => {
        // This approach is a little delicate, as it doesn't handle cases where a provider doesn't have a section
        // There shouldn't be a case that this data is missing, but something to be aware of.
        return getAllPolicyFeatureSectionIds(results, productType).map(
            (sectionId) => {
                const sectionList = getSections(results, productType, sectionId);

                return {
                    id: `FeatureSection-${sectionId}`,
                    type: "standalone",
                    title: sectionList[0]?.sectionName,
                    tooltip: [
                        sectionList[0]?.tooltip,
                        "Cover varies between plans. Please see the policy document for full details."
                    ],
                    component: QuoteTablePolicyFeatureContent,
                    renderOnError: false,
                    cells: sectionList.map(
                        (
                            section
                        ): QuoteTableCellInfo<typeof QuoteTablePolicyFeatureContent> => {
                            return {
                                key: section.key,
                                quoteState: section.quoteState,
                                props: {
                                    detail: section.detail,
                                    detailMore: section.detailMore
                                }
                            };
                        }
                    )
                };
            }
        );
    };

    const getViewSwitcherInfo: IQuoteTableReturn["getViewSwitcherInfo"] = (
        results: IQuoteCardResult[],
        currentView: QuoteTableView,
        setView: (newView: QuoteTableView) => void
    ) => {
        const viewSwitcherSettings = getViewSwitcher(currentView);

        return {
            id: "ViewSwitcher",
            type: "standalone",
            title: null,
            tooltip: null,
            component: QuoteTableViewSwitcherContent,
            renderOnError: true,
            cells: results.map(
                (card): QuoteTableCellInfo<typeof QuoteTableViewSwitcherContent> => {
                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props: {
                            onClickSwitchView: () => setView(viewSwitcherSettings.target),
                            viewSwitcherText: viewSwitcherSettings.text
                        }
                    };
                }
            )
        };
    };

    const getDocumentInfo: IQuoteTableReturn["getDocumentInfo"] = (
        cards: IQuoteCardResult[],
        productType: ProductType
    ) => {
        return {
            id: "Documents",
            type: "standalone",
            title: "Documents",
            tooltip: null,
            component: QuoteTableDocumentContent,
            renderOnError: false,
            cells: cards.map(
                (card): QuoteTableCellInfo<typeof QuoteTableDocumentContent> => {
                    const productInfo: IQuoteCardProduct = card.productInfo[productType];

                    return {
                        key: createKey(card),
                        quoteState: createQuoteTableState(card, globalState),
                        props: {
                            brochureDoc: {
                                documentUrl: productInfo?.documents.brochure.documentUrl,
                                thumbnailUrl: productInfo?.documents.brochure.thumbnailUrl
                            },
                            policyDoc: {
                                documentUrl: productInfo?.documents.policy.documentUrl,
                                thumbnailUrl: productInfo?.documents.policy.thumbnailUrl
                            }
                        }
                    };
                }
            )
        };
    };

    const getFooterInfo: IQuoteTableReturn["getFooterInfo"] = (
        results: IQuoteCardResult[],
        handleSelectProduct: QuoteTableCallToActionFunction,
        currentView: QuoteTableView,
        setView: (newView: QuoteTableView) => void
    ) => {
        const viewSwitcherSettings = getViewSwitcher(currentView);

        return {
            id: "Footer",
            type: "standalone",
            title: null,
            tooltip: null,
            component: QuoteTableFooterContent,
            renderOnError: false,
            cells: results.map(
                (card): QuoteTableCellInfo<typeof QuoteTableFooterContent> => {
                    const handleSelectAllProducts = (): Promise<void> => {
                        const promiseList: Promise<void>[] = Object.values(
                            card.productInfo
                        ).map((s) => {
                            return handleSelectProduct(s.productId);
                        });

                        return Promise.all(promiseList).then();
                    };
                    const quoteState = createQuoteTableState(card, globalState);

                    return {
                        key: createKey(card),
                        quoteState,
                        props: {
                            applicationURL: generateApplicationURL(groupId, card),
                            handleSelectProduct: handleSelectAllProducts,
                            onClickSwitchView: () => setView(viewSwitcherSettings.target),
                            quoteState,
                            totalPremium: card.result.totalPremium || 0,
                            viewSwitcherText: viewSwitcherSettings.text,
                            card,
                            groupId
                        }
                    };
                }
            )
        };
    };

    const getProductDetailsList = (cards: IQuoteCardResult[]): ProductType[] => {
        const isAllResultsInvalid = cards.every((card) => card.result.errors.length > 0);

        if (isAllResultsInvalid) return [];

        return ClientProductTypesList.filter((product) =>
            cards.every(
                (card) => !!card.productInfo[product] || card.state.hasBlockingError
            )
        );
    };

    return {
        getQuoteTableHeaderInfo,
        getQuoteTableHeaderStickyInfo,
        getInsurerInfo,
        getOffersAndBenefitsInfo,
        getProductInfo,
        getFinancialStrengthInfo,
        getCustomerServiceInfo,
        getPolicyFeaturesInfo,
        getFeatureSectionInfoList,
        getViewSwitcherInfo,
        getFooterInfo,
        getDocumentInfo,
        setNewGlobalState,
        getProductDetailsList,
        globalState,
        createQuoteTableState
    };
};

export default useQuoteSections;
